import {
  all, call, put, select, takeEvery, takeLatest
} from "redux-saga/effects";

import { get, STORAGE_KEYS, save } from "API/LocalStorage";
import {
  projectsDeleteSuccess,
  projectsFilterCountSet,
  projectsFilterExpressionAdd,
  projectsFilterExpressionRemove,
  projectsFilterExpressionSet,
  projectsFilterIdListSet,
  projectsFilterKeySet,
  projectsFilterLoad,
  projectsFilterStart,
  projectsRelevanceSuccess,
  uiSetActiveProjectId,
  uiSetIsFiltering,
  uiSetSelectedProjectIds,
  uiUsernamesAdd,
  uiUsernamesRemove
} from "Store/Actions";
import { selectFilterCount, selectFilterExpression, selectFilterKey } from "Store/Selectors/Filter";
import {
  hasInjectedToken, indexOfTokenSet, mergeTokenSets, toExpression, toTokenSets
} from "Utils/Tokenizr";
import { isNullOrWhiteSpace } from "Utils/String";
import { selectProjectList, selectSelectedOrActiveProjectIds } from "Store/Selectors/Projects";
import { IProject } from "Model/Storage/IProject";
import { PayloadAction } from "@reduxjs/toolkit";
import { hasNoElements, intersect, removeElementAt } from "Utils/Array";
import { Configuration } from "Utils/Configuration";
import { filterByExpression } from "Workers/Filter";
import { selectActiveProjectId, selectIsDetailsOpen } from "Store/Selectors/UI";
import { sequentialPromise } from "Utils/Promise";
import { isEmpty } from "Utils/Object";

async function getFilterExpressionByKey(key: string): Promise<string> {
  if (isNullOrWhiteSpace(key)) {
    return ``;
  }
  const config = await Configuration.getInstanceAsync();
  const filterCategoryList = config.getFilterCategoryList();
  const { expression } = filterCategoryList.find(({ translationKey }) => translationKey === key);
  return expression;
}

async function filterExpressionWithKey(key: string, expression: string): Promise<string> {
  const keyExpression = await getFilterExpressionByKey(key);
  return isNullOrWhiteSpace(keyExpression) ? expression : `${expression} ${keyExpression}`.trim();
}

async function countFilter(
  items: IProject[],
  counts: [string, number][],
  forceRecalculate = false
): Promise<[string, number][]> {
  const filterKeyList = Configuration.getFilterCategoryList().map(({ translationKey }) => translationKey);

  return sequentialPromise(filterKeyList, async (key) => {
    if (hasNoElements(items)) {
      return [key, 0];
    }

    const [, storedCount] = counts.find((c) => c[0] === key) ?? [``, -1];
    if (storedCount > 99 && !forceRecalculate) {
      return [key, storedCount];
    }

    const filteredItems = await filterByExpression(
      items,
      `@id`,
      await filterExpressionWithKey(key, ``),
      get<string[]>(STORAGE_KEYS.USERNAMES)
    );
    return [key, filteredItems.length];
  });
}

function* projectsFilterStartSaga() {
  const projectList: IProject[] = yield select(selectProjectList);

  if (isEmpty(projectList)) {
    yield put(projectsFilterIdListSet([]));
    return;
  }

  const [key, expression]: [string, string] = yield all([select(selectFilterKey), select(selectFilterExpression)]);

  save(STORAGE_KEYS.FILTER_KEY, key);
  const expressionWithKey = yield call(filterExpressionWithKey, key, expression);

  const idList: string[] = yield call(
    filterByExpression,
    projectList,
    `@id`,
    expressionWithKey,
    get<string[]>(STORAGE_KEYS.USERNAMES)
  );

  yield put(projectsFilterIdListSet(idList));

  const [selectedProjectIds, activeProjectId, isDetailsOpen] = yield all([
    select(selectSelectedOrActiveProjectIds),
    select(selectActiveProjectId),
    select(selectIsDetailsOpen)
  ]);
  const intersectedIdList = intersect(idList, selectedProjectIds);
  yield put(uiSetSelectedProjectIds(intersectedIdList));
  if (!intersectedIdList.includes(activeProjectId) && !isDetailsOpen) {
    yield put(uiSetActiveProjectId(null));
  }
}

function* projectsFilterExpressionAddSaga(action: PayloadAction<string>) {
  const { payload: newExpression } = action;

  if (isNullOrWhiteSpace(newExpression)) {
    save<string>(STORAGE_KEYS.FILTER_EXPRESSION, ``);
    yield put(projectsFilterExpressionSet(``));
    yield put(projectsFilterStart());
  }

  const currentExpression: string = yield select(selectFilterExpression);

  if (currentExpression.includes(newExpression)) {
    return;
  }

  const nextExpression = hasInjectedToken(newExpression)
    ? `${currentExpression} ${newExpression}`.trim()
    : toExpression(mergeTokenSets(toTokenSets(currentExpression), toTokenSets(newExpression)));

  save<string>(STORAGE_KEYS.FILTER_EXPRESSION, nextExpression);
  yield put(projectsFilterExpressionSet(nextExpression));
  yield put(projectsFilterStart());
}

function* projectsFilterExpressionRemoveSaga(action: PayloadAction<{ fieldName: string; search: string }>) {
  const {
    payload: { fieldName, search }
  } = action;

  const currentExpression = yield select(selectFilterExpression);
  const tokenSetList = toTokenSets(currentExpression);
  const tokenSetIndex = indexOfTokenSet(tokenSetList, fieldName, search);
  const nextExpression = toExpression(removeElementAt(tokenSetList, tokenSetIndex));

  save<string>(STORAGE_KEYS.FILTER_EXPRESSION, nextExpression);
  yield put(projectsFilterExpressionSet(nextExpression));
  yield put(projectsFilterStart());
}

function* projectsFilterLoadSaga() {
  const expression = get<string>(STORAGE_KEYS.FILTER_EXPRESSION);
  const key = get<string>(STORAGE_KEYS.FILTER_KEY);
  yield all([put(projectsFilterExpressionAdd(expression)), put(projectsFilterKeySet(key))]);
}

function* projectsFilterCountSaga(action?: PayloadAction) {
  const [items, count]: [IProject[], [string, number][]] = yield all([
    select(selectProjectList),
    select(selectFilterCount)
  ]);

  let forceRecalculate = false;
  if ([projectsDeleteSuccess.type, uiUsernamesRemove.type].includes(action?.type)) {
    forceRecalculate = true;
  }

  const filterCount = yield call(countFilter, items, count, forceRecalculate);
  yield put(projectsFilterCountSet(filterCount));
}

export function* recalculateFiltersSaga(action?: PayloadAction) {
  yield all([uiSetIsFiltering(true), projectsFilterCountSaga(action), projectsFilterStartSaga()]);
}

export function* FilterSaga(): Generator<unknown, void> {
  yield all([
    takeLatest([projectsFilterLoad, projectsRelevanceSuccess], projectsFilterLoadSaga),
    takeLatest([projectsFilterStart, projectsFilterKeySet], projectsFilterStartSaga),
    takeEvery(projectsFilterExpressionAdd, projectsFilterExpressionAddSaga),
    takeEvery(projectsFilterExpressionRemove, projectsFilterExpressionRemoveSaga),
    takeEvery([projectsDeleteSuccess, uiUsernamesAdd, uiUsernamesRemove], recalculateFiltersSaga)
  ]);
}
